import Decimal from "decimal.js";
import { DropdownButton, DropdownItem, DropdownMenu } from "solid-bootstrap";
import { Component, createEffect, createSignal, For, Index, onMount, ParentComponent, Show } from "solid-js";
import { z } from "zod";
import {
  isDeductedTax,
  measurementUnit,
  measurementUnitNames, paymentType, posPayment, submitAmountForSignature,
  taxTypeNames,
  vatCategory,
  vatCategoryRate,
  vatRates
} from "~/mydata";
import {
  calculateRemainingAmount,
  itemDiscount,
  itemGrossTotal,
  itemGrossValue,
  itemNetValue,
  itemTaxAmount,
  itemTaxEntries,
  itemTotalDeductedTaxAmount,
  itemTotalNetValue,
  itemTotalTaxAmount,
  itemTotalVAT,
  itemVAT,
  itemVATSummary
} from "~/onesign/invoice-totals";
import { findTax } from "~/onesign/taxes";
import { InvoiceItemSchema, invoiceSchema, supportedInvoiceTypes } from "~/schemas/invoices";
import { replaceDotWithComma } from "./helpers";
import InvoicePayment from "./InvoicePayment";
import Button from "~/components/Button";
import PosPayForm from "~/components/PosPayForm";
import { createModal } from "~/components/modal";
import { useRPC } from "~/components/RPCProvider";
import { useValidationState } from "~/zod-forms";
import * as net from "net";

type InvoiceItem = z.input<InvoiceItemSchema>;

const InvoiceItemRowForm: Component<{
  items: InvoiceItem[];
  editItem(index: number): void;
  deleteItem(index: number): void;
  draftInvoiceId: string;
  invoiceType: string;
  issuerVatNumber: string;
  taxSeriesId: string | undefined;
  saveDraftInvoice(): any;
  setToggleFetchPayments(fetch: boolean): void;
  toggleFetchPayments: boolean;
  paidAmt: any;
}> = (props) => {
  const rpc = useRPC();
  const [posModal, PosModal] = createModal(true);
  const [paymentMeans, setPaymentMeans] = createSignal(0);  // cash
  const [paymentStatus, setPaymentStatus] = createSignal("Pay");

  const [grossValue, setGrossValue] = createSignal(itemGrossTotal(...props.items));
  const [netValue, setNetValue] = createSignal(itemTotalNetValue(...props.items));
  const [totalVatValue, setTotalVatValue] = createSignal(itemTotalVAT(...props.items));
  const [remainingAmount, setRemainingAmount] = createSignal("");
  const [payingAmount, setPayingAmount] = createSignal("");
  const [payableAmount, setPayableAmount] = createSignal("");

  const form = useValidationState<typeof invoiceSchema>();
  const [paymentChoice, setPaymentChoice] = form.bind(
    `payments[0]` as "payments[0]"
  );

  const getGrossValue = () => itemGrossTotal(...props.items);
  const getTotalNetValue = () => itemTotalNetValue(...props.items);
  const getTotalVatValue = () => itemTotalVAT(...props.items);

  createEffect(() => {
    setRemainingAmount(calculateRemainingAmount(getGrossValue()?.toString(), props.paidAmt()))
  })

  createEffect(() => {
    setPayableAmount(remainingAmount())
  })

  createEffect(() => {
    setPaymentMeans(paymentChoice().type)
  })

  createEffect(() => {
    setGrossValue(getGrossValue());
    setPayingAmount(getGrossValue()?.toString());
    setNetValue(getTotalNetValue());
    setTotalVatValue(getTotalVatValue());
  }, [props.items.length])

  const editItem = (index: number) => props.editItem(index);
  const deleteItem = (index: number) => props.deleteItem(index);
  const InvoiceItemRow = (props: { item: InvoiceItem; index: number }) => {
    return (
      <>
        <tr class="table-row">
          <td class="ps-3 text-center">
            <DropdownButton
              variant="secondary"
              size="lg"
              title={props.index + 1}
              menuVariant="dropdown"
              class="p-1 mb-2 mt-2"
            >
              <DropdownMenu>
                <DropdownItem
                  class="p-2"
                  onClick={(e) => {
                    e.preventDefault();
                    editItem(props.index);
                  }}
                >
                  Επεξεργασία
                </DropdownItem>
                <DropdownItem
                  class="text-danger p-2"
                  onClick={(e) => {
                    e.preventDefault();
                    deleteItem(props.index);
                  }}
                >
                  Διαγραφή
                </DropdownItem>
              </DropdownMenu>
            </DropdownButton>
          </td>
          <td class="text-center">
            <p>
              {props.item.code}
              {props.item.code ? " | " : " "}
              {props.item.description}
            </p>
          </td>
          <td class="text-center">
            {
              replaceDotWithComma(
                new Decimal(props.item.netValuePerUnit ?? "0").toFixed(2)
              )
            }
          </td>
          <td class="text-center">
            {props.item.measurementUnit ? (
              <span class="text-muted">
                {measurementUnitNames[props.item.measurementUnit].slice(0, 3)}
                {". "}
              </span>
            ) : (
              ""
            )}
            {new Decimal(props.item.quantity ?? "0").toFixed(
              (props.item.measurementUnit ?? measurementUnit.Items) ===
                measurementUnit.Items
                ? 0
                : 2
            )}{" "}
          </td>
          <td class="text-center">
            {
              replaceDotWithComma(
                itemNetValue(props.item)?.toFixed(2) ?? ""
              )
            }
          </td>
          <td class="text-center">
            {
              replaceDotWithComma(
                itemDiscount(props.item)?.toFixed(2) ?? new Decimal(0).toFixed(2)
              )
            }
          </td>
          <td class="text-center">
            <span class="text-muted">
              {vatCategoryRate(props.item.vat.vatCategory) + "%"}
            </span>{" "}
            {
              replaceDotWithComma(
                itemVAT(props.item)?.toFixed(2) ?? "-"
              )
            }
          </td>
          <td class="text-center">
            {
              replaceDotWithComma(
                itemTotalTaxAmount(props.item)?.toFixed(2) ?? ""
              )
            }
          </td>
          <td class="text-center">
            {
              replaceDotWithComma(
                itemTotalDeductedTaxAmount(props.item)?.toFixed(2) ?? ""
              )
            }
          </td>
          <td class="text-center">
            {
              replaceDotWithComma(
                itemGrossValue(props.item)?.toFixed(2) ?? ""
              )
            }
          </td>
        </tr>
        <Show when={(props.item.taxes ?? []).length > 0}>
          <For each={itemTaxEntries(props.item)}>
            {([tax, item]) => {
              return (
                <tr class="table-row">
                  <td class="text-center"></td>
                  <td class="text-left">
                    <div>
                      <span class="badge bg-secondary p-2 me-2">
                        {taxTypeNames[tax.type]}
                      </span>{" "}
                      {findTax(tax.type, tax.category)?.title ?? ""}
                    </div>
                  </td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-center"></td>
                  <td class="text-muted text-center">
                    {isDeductedTax(tax.type)
                      ? ""
                      : replaceDotWithComma(
                          itemTaxAmount(tax, item)?.toFixed(2)
                        )
                    }
                  </td>
                  <td class="text-center text-muted">
                    {isDeductedTax(tax.type)
                      ? replaceDotWithComma(
                          itemTaxAmount(tax, item)?.toFixed(2)
                        )
                      : ""
                    }
                  </td>
                  <td></td>
                </tr>
              );
            }}
          </For>
        </Show>
      </>
    );
  };
  const SummaryRow: ParentComponent<{
    amount?: Decimal;
    classList?: Record<string, boolean>;
  }> = (props) => (
    <tr classList={props.classList}>
      <td colspan="6" class="border-bottom-0 pt-2 pb-2" />
      <th scope="row" colspan="3" class="text-start pt-2 pb-2">
        {props.children}
      </th>
      <td class="text-end fs-5 pt-2 pb-2 pe-3">
        {
          replaceDotWithComma(
            props.amount?.toFixed(2) ?? ""
          )
        }
      </td>
    </tr>
  );
  const SummaryDetailsTable: ParentComponent<{ label: string }> = (props) => (
    <details>
      <summary>{props.label}</summary>
      <table class="table table-sm table-borderless">
        <tbody>{props.children}</tbody>
      </table>
    </details>
  );

  const pollPaymentStatus = async (payId: string) => {
    // TODO remove as it will be done by the POS
    await rpc.mutation("updatePayment", {
      id: payId,
      status: "success",
    });

    setPaymentStatus("Pay");
    props.setToggleFetchPayments(!props.toggleFetchPayments);
    posModal.hide();
  };

  const makePayment = async () => {
    if (paymentMeans() === paymentType.POS) {
      setPaymentStatus(submitAmountForSignature);
      // save the invoice as draft
      try {
        const draftInvoice = await props.saveDraftInvoice();
      } catch (e) {
        console.log(e);
      }

      const paymentId = await posPayment(
        props.draftInvoiceId,
        "",
        (new Date()).toString(),
        payableAmount(),
        netValue()?.toFixed(2),
        totalVatValue()?.toFixed(2),
        grossValue()?.toFixed(2),
        "Term_47386",
        setPaymentStatus,
        rpc,
        props.invoiceType
      );

      await pollPaymentStatus(paymentId);
    } else {
      setPayingAmount(payableAmount().toString());
      posModal.hide()
    }

  }


  return (
    <div class="pb-2" style="overflow:auto;">
      <table class="table">
        <thead class="primary-color-bg text-light">
          <tr class="fs-6 table-row text-center">
            <th class="ps-2 pe-2">AA</th>
            <th class="ps-2 pe-2">Περιγραφή</th>
            <th class="ps-2 pe-2">Τιμή μονάδας (€)</th>
            <th class="ps-2 pe-2">Ποσότητα (€)</th>
            <th class="ps-2 pe-2">Καθαρή Αξία (€)</th>
            <th class="ps-2 pe-2">Έκπτωση (€)</th>
            <th class="ps-2 pe-2">Φ.Π.Α (€)</th>
            <th class="ps-2 pe-2">Φόροι (€)</th>
            <th class="ps-2 pe-2">Κρατήσεις (€)</th>
            <th class="ps-2 pe-2">Σύνολο (€)</th>
          </tr>
        </thead>
        <tbody>
          <Index each={props.items ?? []}>
            {(item, index) => <InvoiceItemRow item={item()} index={index} />}
          </Index>
        </tbody>
        <tfoot>
          <SummaryRow amount={netValue()}>
            <SummaryDetailsTable label="Συνολική Καθαρή Αξία">
              <tr class="fw-normal text-muted text-start">
                <th scope="row" class="pt-1 pb-1">
                  Κρατήσεις
                </th>
                <td class="pt-1 pb-1">
                  {
                    replaceDotWithComma(
                      itemTotalDeductedTaxAmount(...props.items).toFixed(2)
                    )
                  }
                </td>
              </tr>
            </SummaryDetailsTable>
          </SummaryRow>
          <SummaryRow amount={itemTotalTaxAmount(...props.items)}>
            Σύνολο Φόρων
          </SummaryRow>
          <SummaryRow amount={totalVatValue()}>
            <SummaryDetailsTable label="Συνολική Αξία Φ.Π.Α">
              <For each={itemVATSummary(...props.items)}>
                {([category, amount]) => (
                  <tr class="fw-normal text-muted text-start">
                    <th class="pt-1 pb-1" scope="row">
                      Φ.Π.Α. {vatRates[category]}%
                    </th>
                    <td class="pt-1 pb-1">{
                      replaceDotWithComma(
                        amount.toFixed(2)
                      )
                    }</td>
                  </tr>
                )}
              </For>
            </SummaryDetailsTable>
          </SummaryRow>
          <SummaryRow
            amount={+remainingAmount()}
          >
            Remaining Amount
          </SummaryRow>
          <SummaryRow
            classList={{ "fs-6": true, "text-danger": true }}
            amount={+payingAmount()}
          >
            You are paying
          </SummaryRow>
          <SummaryRow
            classList={{ "fs-5": true }}
            amount={grossValue()}
          >
            Συνολική Αξία
          </SummaryRow>
          <tr>
            <td colSpan="6" class="border-bottom-0" />
            <td colspan="2" class="pt-3">
              <InvoicePayment index={0} setPaymentMeans={setPaymentMeans} remainingAmount={remainingAmount} payingAmount={payingAmount} />
            </td>
            <Show when={paymentMeans() !== paymentType.Credit && parseFloat(remainingAmount()) > 0} fallback={<></>} keyed={true}>
              <td colspan="2" class={"pt-5"}>
                <div class={"d-flex justify-content-center align-items-center"}>
                  <Button
                    name="pay"
                    type={"button"}
                    kind="primary"
                    class="form-control bottom-0 w-75"
                    disabled={props.taxSeriesId === undefined}
                    onClick={() => posModal.show()}
                  >
                    Adjust Paying Amount
                  </Button>
                </div>
              </td>
            </Show>
          </tr>
        </tfoot>
      </table>

      <PosModal
        centered
        dialogClass="w100per justify-content-center"
        contentClass="w30per mobile-w100per p-3"
        size="sm"
      >
        <PosModal.Body>
          <PosPayForm
            payableAmount={payableAmount}
            setPayAmount={setPayableAmount}
            remainingAmt={remainingAmount()}
            paymentStatus={paymentStatus}
            makePayment={makePayment}
            paymentMeans={paymentMeans}
            setPayingAmount={setPayingAmount}
          />
        </PosModal.Body>
      </PosModal>
    </div>
  );
};

export default InvoiceItemRowForm;
